import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export type RegisteredAgent = {
  id: string
  agent_kind: string
  service_provider_name: string | null
  street_address: string | null
  city: string | null
  state_province: string | null
  postal_code: string | null
  country_code: string | null
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  formationId: string
  registeredAgent: RegisteredAgent | null
}

// The wizard appoints Corppy as agent but deliberately leaves the address
// blank rather than fabricate one — this is where the real address goes.
// Data is nested under `registered_agent` explicitly (not left to Rails'
// param-wrapping) because a top-level ::RegisteredAgent model already
// exists for the standalone registered-agent product — wrap_parameters
// infers its attribute list, not Formation::RegisteredAgent's, and drops
// every field this form actually has.
export function RegisteredAgentAddressDialog({ open, onOpenChange, formationId, registeredAgent }: Props) {
  const { data, setData, patch, processing, errors, reset, clearErrors } = useForm({
    registered_agent: {
      street_address: '',
      city: '',
      state_province: '',
      postal_code: '',
      country_code: 'US'
    }
  })

  useEffect(() => {
    if (open && registeredAgent) {
      setData('registered_agent', {
        street_address: registeredAgent.street_address || '',
        city: registeredAgent.city || '',
        state_province: registeredAgent.state_province || '',
        postal_code: registeredAgent.postal_code || '',
        country_code: registeredAgent.country_code || 'US'
      })
      clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, registeredAgent])

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) {
      reset()
      clearErrors()
    }
  }

  function setField(field: keyof typeof data.registered_agent, value: string) {
    setData('registered_agent', { ...data.registered_agent, [field]: value })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!registeredAgent) return
    patch(`/backoffice/formations/${formationId}/registered_agents/${registeredAgent.id}`, {
      onSuccess: () => handleClose(true)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-md overflow-y-auto">
        <DialogHeader className="mb-6">
          <DialogTitle>Registered agent address</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          <p className="text-xs text-muted-foreground">
            {registeredAgent?.service_provider_name || 'Corppy'} — the real, per-state street address, not a P.O. box.
          </p>

          <div className="space-y-2">
            <Label htmlFor="ra_street_address">
              Street address <span className="text-destructive">*</span>
            </Label>
            <Input id="ra_street_address" value={data.registered_agent.street_address} onChange={(e) => setField('street_address', e.target.value)} required />
            {errors['registered_agent.street_address'] && <p className="text-sm text-destructive">{errors['registered_agent.street_address']}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ra_city">
              City <span className="text-destructive">*</span>
            </Label>
            <Input id="ra_city" value={data.registered_agent.city} onChange={(e) => setField('city', e.target.value)} required />
            {errors['registered_agent.city'] && <p className="text-sm text-destructive">{errors['registered_agent.city']}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ra_state_province">State</Label>
              <Input id="ra_state_province" value={data.registered_agent.state_province} onChange={(e) => setField('state_province', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ra_postal_code">Postal code</Label>
              <Input id="ra_postal_code" value={data.registered_agent.postal_code} onChange={(e) => setField('postal_code', e.target.value)} />
            </div>
          </div>

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={processing}>
                {processing ? 'Saving...' : 'Save address'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
